import React from 'react';
import { Alert, Box, Grid, Skeleton, Stack, Typography } from '@mui/material';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { StorefrontService } from '../../../types/additionalServiceTypes';
import ServiceCard from './ServiceCard';

interface Props {
  services: StorefrontService[];
  loading: boolean;
  error: string | null;
  onServiceSelect: (service: StorefrontService) => void;
}

function ServiceCardSkeleton() {
  return (
    <Box sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', bgcolor: '#fff', p: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Skeleton variant="rounded" width={48} height={48} sx={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Skeleton width="70%" height={20} />
          <Skeleton width="90%" height={14} sx={{ mt: 0.75 }} />
          <Skeleton width="55%" height={14} sx={{ mt: 0.5 }} />
        </Box>
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Skeleton width={80} height={26} />
        <Skeleton width={90} height={24} sx={{ borderRadius: 4 }} />
      </Box>
      <Skeleton variant="rounded" height={32} sx={{ borderRadius: 2 }} />
    </Box>
  );
}

function ServiceList({ services, loading, error, onServiceSelect }: Props) {
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ px: { xs: 2, md: 3 }, py: 3, bgcolor: '#f4f6f8', minHeight: '100%' }}>
        <Grid container spacing={2.5}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <ServiceCardSkeleton />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (services.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 10,
          color: 'text.secondary',
          px: 2,
          bgcolor: '#f4f6f8',
          minHeight: '100%',
        }}
      >
        <SearchOffIcon sx={{ fontSize: 56, mb: 2, opacity: 0.35 }} />
        <Typography variant="body1" fontWeight={600} sx={{ mb: 0.5 }}>
          Nenhum serviço encontrado
        </Typography>
        <Typography variant="body2" textAlign="center" color="text.secondary">
          Tente ajustar os filtros ou ampliar o raio de busca.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3, bgcolor: '#f4f6f8', minHeight: '100%' }}>
      <Grid container spacing={2.5}>
        {services.map((service) => (
          <Grid item xs={12} sm={6} md={4} key={service.id}>
            <ServiceCard service={service} onSelect={onServiceSelect} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default ServiceList;
